import React, {useState, useRef, useEffect} from 'react';
import {useLocation, useNavigate, Link} from 'react-router-dom';
import {Bell, User, Settings, LogOut, HelpCircle, ChevronDown, Globe, Check} from 'lucide-react';
import {useAuth, ROLES} from '../../context/AuthContext';
import {useCompanyTheme} from '../../context/CompanyThemeContext';
import {getAlertStats} from '../../utils/helper';
import {useTranslation} from '../../context/LanguageContext';

export default function Header() {
    const location = useLocation();
    const navigate = useNavigate();
    const {user, logout} = useAuth();
    const {theme} = useCompanyTheme();
    const {t, language, setLanguage, availableLanguages} = useTranslation();
    const [showDropdown, setShowDropdown] = useState(false);
    const [showNotifications, setShowNotifications] = useState(false);
    const [showLanguages, setShowLanguages] = useState(false);
    const dropdownRef = useRef(null);
    const notifRef = useRef(null);
    const langRef = useRef(null);

    const alertStats = getAlertStats();

    const pageTitles = {
        '/': {title: t('dashboard.title'), subtitle: t('dashboard.subtitle')},
        '/loans': {title: t('loans.title'), subtitle: t('loans.subtitle')},
        '/alerts': {title: t('alerts.title'), subtitle: t('alerts.subtitle')},
        '/reports': {title: t('reports.title'), subtitle: t('reports.subtitle')},
        '/import': {title: t('import.title'), subtitle: t('import.subtitle')},
        '/settings': {title: t('settings.title'), subtitle: t('settings.subtitle')},
        '/profile': {title: t('profile.title'), subtitle: t('profile.subtitle')},
        '/help': {title: t('help.title'), subtitle: t('help.subtitle')},
        '/team': {title: t('team.title'), subtitle: t('team.subtitle')},
        '/company': {title: t('companySettings.title'), subtitle: t('companySettings.subtitle')},
        '/notifications': {title: t('notifications.title'), subtitle: t('notifications.subtitle')},
        '/roles': {title: t('roles.title'), subtitle: t('roles.subtitle')},
        '/waivers': {title: t('waivers.title'), subtitle: t('waivers.subtitle')},
    };


    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
            if (notifRef.current && !notifRef.current.contains(event.target)) {
                setShowNotifications(false);
            }
            if (langRef.current && !langRef.current.contains(event.target)) {
                setShowLanguages(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const getPageInfo = () => {

        if (location.pathname.startsWith('/loans/') && location.pathname !== '/loans') {
            return {title: t('loanDetails.title'), subtitle: t('loanDetails.subtitle')};
        }
        return pageTitles[location.pathname] || {title: '', subtitle: ''};
    };

    const getInitials = () => {
        if (!user) return '??';
        return `${user.firstName?.charAt(0) || ''}${user.lastName?.charAt(0) || ''}`.toUpperCase();
    };

    const getRoleLabel = () => {
        switch (user?.role) {
            case ROLES.AGENT:
                return t('roles.agent') || 'Facility Agent';
            case ROLES.LENDER:
                return t('roles.lender') || 'Lender';
            case ROLES.BORROWER:
                return t('roles.borrower') || 'Borrower';
            default:
                return user?.role || 'User';
        }
    };

    const pageInfo = getPageInfo();

    return (
        <header className="bg-white border-b border-slate-200 px-6 py-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    {}
                    {theme.logo && (
                        <div className="flex items-center gap-3 pr-4 border-r border-slate-200">
                            <img
                                src={theme.logo}
                                alt={theme.companyName}
                                className="h-10 w-auto max-w-[120px] object-contain"
                            />
                        </div>
                    )}
                    <div>
                        <h2 className="text-lg font-semibold text-slate-800">{pageInfo.title}</h2>
                        <p className="text-sm text-slate-500">{pageInfo.subtitle}</p>
                    </div>
                </div>

                <div className="flex items-center gap-3">

                    {}
                    <div className="relative" ref={langRef}>
                        <button
                            onClick={() => setShowLanguages(!showLanguages)}
                            className="flex items-center gap-2 p-2 hover:bg-slate-100 rounded-lg transition-colors"
                            title={t('settings.language')}
                        >
                            <Globe size={20} className="text-slate-600" />
                            <span className="text-sm font-medium text-slate-600 hidden sm:inline">
                {availableLanguages.find(l => l.code === language)?.flag}
              </span>
                            <ChevronDown
                                size={14}
                                className={`text-slate-400 transition-transform hidden sm:inline ${showLanguages ? 'rotate-180' : ''}`}
                            />
                        </button>

                        {}
                        {showLanguages && (
                            <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-slate-200 py-2 z-50">
                                <div className="px-4 py-2 border-b border-slate-100">
                                    <h3 className="font-semibold text-slate-800 text-sm">{t('settings.language')}</h3>
                                </div>
                                <div className="py-1">
                                    {availableLanguages.map((lang) => (
                                        <button
                                            key={lang.code}
                                            onClick={() => {
                                                setLanguage(lang.code);
                                                setShowLanguages(false);
                                            }}
                                            className={`w-full flex items-center justify-between px-4 py-2 text-sm transition-colors ${
                                                language === lang.code
                                                    ? 'bg-blue-50 text-blue-700'
                                                    : 'text-slate-700 hover:bg-slate-50'
                                            }`}
                                        >
                      <span className="flex items-center gap-3">
                        <span className="text-lg">{lang.flag}</span>
                        <span>{lang.name}</span>
                      </span>
                                            {language === lang.code && (
                                                <Check size={16} className="text-blue-600" />
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {}
                    <div className="relative" ref={notifRef}>
                        <button
                            onClick={() => setShowNotifications(!showNotifications)}
                            className="relative p-2 hover:bg-slate-100 rounded-lg transition-colors"
                        >
                            <Bell size={20} className="text-slate-600"/>
                            {alertStats.unread > 0 && (
                                <span
                                    className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 text-xs font-bold bg-red-500 text-white rounded-full flex items-center justify-center">
                  {alertStats.unread > 99 ? '99+' : alertStats.unread}
                </span>
                            )}
                        </button>

                        {}
                        {showNotifications && (
                            <div
                                className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-lg border border-slate-200 py-2 z-50">
                                <div className="px-4 py-2 border-b border-slate-100">
                                    <div className="flex items-center justify-between">
                                        <h3 className="font-semibold text-slate-800">{t('header.notifications')}</h3>
                                        {alertStats.unread > 0 && (
                                            <span
                                                className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-600 rounded-full">
                        {alertStats.unread} {t('header.unread')}
                      </span>
                                        )}
                                    </div>
                                </div>

                                <div className="max-h-64 overflow-y-auto">
                                    {alertStats.critical > 0 && (
                                        <div
                                            className="px-4 py-3 hover:bg-slate-50 cursor-pointer border-l-4 border-red-500">
                                            <p className="text-sm font-medium text-slate-800">{t('header.criticalAlerts')}</p>
                                            <p className="text-xs text-slate-500">{alertStats.critical} {t('header.covenantsInBreach')}</p>
                                        </div>
                                    )}
                                    {alertStats.warning > 0 && (
                                        <div
                                            className="px-4 py-3 hover:bg-slate-50 cursor-pointer border-l-4 border-amber-500">
                                            <p className="text-sm font-medium text-slate-800">{t('header.warnings')}</p>
                                            <p className="text-xs text-slate-500">{alertStats.warning} {t('header.covenantsApproachingThreshold')}</p>
                                        </div>
                                    )}
                                    {alertStats.critical === 0 && alertStats.warning === 0 && (
                                        <div className="px-4 py-6 text-center">
                                            <Bell className="w-8 h-8 text-slate-300 mx-auto mb-2"/>
                                            <p className="text-sm text-slate-500">{t('header.noNotifications')}</p>
                                        </div>
                                    )}
                                </div>

                                <div className="px-4 py-2 border-t border-slate-100">
                                    <Link
                                        to="/alerts"
                                        onClick={() => setShowNotifications(false)}
                                        className="block text-center text-sm text-blue-600 hover:text-blue-700 font-medium"
                                    >
                                        {t('header.viewAllAlerts')}
                                    </Link>
                                </div>
                            </div>
                        )}
                    </div>

                    {}
                    <div className="w-px h-8 bg-slate-200"/>

                    {}
                    <div className="relative" ref={dropdownRef}>
                        <button
                            onClick={() => setShowDropdown(!showDropdown)}
                            className="flex items-center gap-2 p-2 hover:bg-slate-100 rounded-lg transition-colors"
                        >
                            <div
                                className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                                {getInitials()}
                            </div>
                            <div className="text-sm text-left hidden md:block">
                                <p className="font-medium text-slate-800">
                                    {user?.firstName} {user?.lastName}
                                </p>
                                <p className="text-slate-500 text-xs">{getRoleLabel()}</p>
                            </div>
                            <ChevronDown
                                size={16}
                                className={`text-slate-400 transition-transform ${showDropdown ? 'rotate-180' : ''}`}
                            />
                        </button>

                        {}
                        {showDropdown && (
                            <div
                                className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-slate-200 py-2 z-50">
                                {}
                                <div className="px-4 py-3 border-b border-slate-100">
                                    <p className="font-medium text-slate-800">
                                        {user?.firstName} {user?.lastName}
                                    </p>
                                    <p className="text-sm text-slate-500">{user?.email}</p>
                                </div>

                                {}
                                <div className="py-1">
                                    <Link
                                        to="/profile"
                                        onClick={() => setShowDropdown(false)}
                                        className="flex items-center gap-3 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                                    >
                                        <User size={16} className="text-slate-400"/>
                                        {t('header.myProfile')}
                                    </Link>
                                    <Link
                                        to="/settings"
                                        onClick={() => setShowDropdown(false)}
                                        className="flex items-center gap-3 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                                    >
                                        <Settings size={16} className="text-slate-400"/>
                                        {t('header.settings')}
                                    </Link>
                                    <Link
                                        to="/help"
                                        onClick={() => setShowDropdown(false)}
                                        className="flex items-center gap-3 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                                    >
                                        <HelpCircle size={16} className="text-slate-400"/>
                                        {t('header.help')}
                                    </Link>
                                </div>

                                {}
                                <div className="border-t border-slate-100 pt-1">
                                    <button
                                        onClick={handleLogout}
                                        className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                                    >
                                        <LogOut size={16}/>
                                        {t('header.logout')}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </header>
    );
}